import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import {
  connectionStatus,
  useConnectionStore,
} from '@/features/realtime/connection-store'
import { Button } from '@/shared/ui/button'

/**
 * Says so when what is on screen has stopped being live.
 *
 * Only `reconnecting` and `failed` are announced. The fourth status, `offline`,
 * is the quiet one: it is also the state before the first connection has been
 * attempted, and the state a backgrounded tab deliberately parks in, so
 * announcing it would put a warning in front of someone whose connection is
 * fine and about to open.
 */
export function ConnectionBanner() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const status = useConnectionStore(connectionStatus)
  const outdated = useConnectionStore((state) => state.workspaceOutdated)
  const setWorkspaceOutdated = useConnectionStore((state) => state.setWorkspaceOutdated)

  if (outdated) {
    return (
      <Banner
        message={t('connection.outdated')}
        action={t('action.refresh')}
        // The socket could not replay what was missed, so no event will correct
        // what is on screen and only a refetch will. Everything is invalidated
        // rather than a chosen set of keys: the whole point of this state is that
        // the client does not know what changed.
        onAction={() => {
          setWorkspaceOutdated(false)
          void queryClient.invalidateQueries()
        }}
      />
    )
  }

  if (status === 'reconnecting') {
    return <Banner message={t('connection.reconnecting')} />
  }

  if (status === 'failed') {
    return (
      <Banner
        message={t('connection.failed')}
        action={t('action.reload')}
        // A full reload rather than another socket attempt. The attempt budget
        // is spent, and the usual reason is a credential the socket cannot renew
        // on its own -- which a reload fixes, because it re-establishes the
        // session before anything else runs.
        onAction={() => window.location.reload()}
      />
    )
  }

  return null
}

function Banner({
  message,
  action,
  onAction,
}: {
  message: string
  action?: string
  onAction?: () => void
}) {
  return (
    <div
      role="status"
      className="flex items-center justify-center gap-3 bg-amber-100 px-4 py-1.5 text-sm text-amber-950 dark:bg-amber-950 dark:text-amber-100"
    >
      <span>{message}</span>
      {action !== undefined && (
        <Button variant="link" size="sm" className="h-auto p-0" onClick={onAction}>
          {action}
        </Button>
      )}
    </div>
  )
}
